/**
 * Compliance Toolbar Component
 * PROJ-082 - Privacy Governance Canvas Integration (FASE 5)
 *
 * Floating toolbar at the top of the canvas showing flow-level
 * compliance summary: score bar, node governance count, DPIA count,
 * cross-border count, and future report generation button.
 *
 * Uses the getFlowSummary API and shadcn/ui components.
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  ShieldCheck,
  Globe,
  FileSearch,
  FileBarChart,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { getFlowSummary } from '@/lib/privacyGovernanceApi';
import type { FlowComplianceSummary } from '@/types/privacy-governance';

// Lazy-load the report dialog for code splitting
const GenerateReportDialog = lazy(() =>
  import('./GenerateReportDialog').then((m) => ({ default: m.GenerateReportDialog }))
);

interface ComplianceToolbarProps {
  /** Visual flow / workflow ID */
  flowId: string;
}

/** Get score color class */
function getScoreColor(score: number): string {
  if (score > 80) return 'text-emerald-600 dark:text-emerald-400';
  if (score > 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/** Get progress bar color class */
function getProgressColor(score: number): string {
  if (score > 80) return '[&>div]:bg-emerald-500';
  if (score > 50) return '[&>div]:bg-amber-500';
  return '[&>div]:bg-red-500';
}

/** Get score badge variant */
function getScoreBadgeClass(score: number): string {
  if (score > 80) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100';
  if (score > 50) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-100';
}

export function ComplianceToolbar({ flowId }: ComplianceToolbarProps) {
  const canRead = usePermission('privacy_governance', 'READ');
  const canExport = usePermission('privacy_governance', 'EXPORT');
  const [summary, setSummary] = useState<FlowComplianceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!flowId || !canRead) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getFlowSummary(flowId);
      setSummary(data);
    } catch (err) {
      // Silently handle 404 (no governance data yet)
      const message = err instanceof Error ? err.message : 'Error desconocido';
      if (!message.includes('404') && !message.includes('not found')) {
        setError(message);
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [flowId, canRead]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Don't render if user lacks permission
  if (!canRead) return null;

  // Don't render if no data and not loading
  if (!summary && !loading) return null;

  const score = summary?.overallScore ?? 0;
  const dpiaPending = summary ? summary.dpiaRequired - summary.dpiaCompleted : 0;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-3 px-3 py-1.5 bg-background/95 border border-border rounded-lg shadow-sm backdrop-blur-sm">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Cargando...</span>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <AlertTriangle className="h-3 w-3" />
            <span>Error</span>
          </div>
        )}

        {/* Main content when summary is available */}
        {summary && !loading && (
          <>
            {/* Compliance score */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 min-w-[120px]">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className={cn('text-xs font-bold tabular-nums', getScoreColor(score))}>
                      {score}%
                    </span>
                    <Progress
                      value={score}
                      className={cn('h-1.5 w-16 flex-1', getProgressColor(score))}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <p className="font-semibold">Puntaje de cumplimiento del flujo</p>
                <p className="text-muted-foreground">
                  {score > 80 ? 'Cumple' : score > 50 ? 'Parcial' : 'No cumple'}
                </p>
              </TooltipContent>
            </Tooltip>

            <div className="w-px h-4 bg-border" />

            {/* Node governance count */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 gap-1 font-normal cursor-default"
                >
                  {summary.nodesWithGovernance > 0 ? (
                    <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" />
                  ) : (
                    <Info className="h-2.5 w-2.5 text-muted-foreground" />
                  )}
                  {summary.nodesWithGovernance}/{summary.totalNodes} nodos
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {summary.nodesWithGovernance} de {summary.totalNodes} nodos con gobernanza configurada
              </TooltipContent>
            </Tooltip>

            {/* DPIA pending count - only show if there are any */}
            {summary.dpiaRequired > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    className={cn(
                      'text-[10px] px-1.5 py-0 gap-1 font-normal cursor-default',
                      dpiaPending > 0
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100'
                    )}
                  >
                    <FileSearch className="h-2.5 w-2.5" />
                    {dpiaPending > 0
                      ? `${dpiaPending} EIPD pendiente${dpiaPending > 1 ? 's' : ''}`
                      : `${summary.dpiaCompleted} EIPD completa${summary.dpiaCompleted > 1 ? 's' : ''}`}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {summary.dpiaRequired} evaluaciones requeridas, {summary.dpiaCompleted} completadas
                </TooltipContent>
              </Tooltip>
            )}

            {/* Cross-border count - only show if there are any */}
            {summary.crossBorderNodes > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 gap-1 font-normal cursor-default"
                  >
                    <Globe className="h-2.5 w-2.5 text-blue-500" />
                    {summary.crossBorderNodes} transferencia{summary.crossBorderNodes > 1 ? 's' : ''} intl.
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {summary.crossBorderNodes} nodo{summary.crossBorderNodes > 1 ? 's' : ''} con transferencia transfronteriza
                </TooltipContent>
              </Tooltip>
            )}

            <div className="w-px h-4 bg-border" />

            {/* Generate Report button */}
            {canExport && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReportDialogOpen(true)}
                    className="h-6 px-2 text-[10px] gap-1"
                  >
                    <FileBarChart className="h-3 w-3" />
                    Generar Reporte
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Generar reporte ROPA/RAT/RIPD de cumplimiento
                </TooltipContent>
              </Tooltip>
            )}

            {/* Refresh button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchSummary}
                  disabled={loading}
                  className="h-6 w-6 p-0"
                >
                  <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Actualizar resumen de cumplimiento
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>

      {/* Report dialog (lazy loaded) */}
      {reportDialogOpen && (
        <Suspense fallback={null}>
          <GenerateReportDialog
            flowId={flowId}
            open={reportDialogOpen}
            onOpenChange={setReportDialogOpen}
          />
        </Suspense>
      )}
    </TooltipProvider>
  );
}
